'use client'

import { useState, useEffect, useRef } from 'react'
import { clinicasService } from '../../services/clinicas.service'
import type { EstadoItem, CidadeItem, BairroItem, ClinicaItem } from '../../services/clinicas.service'
import { SearchableSelect } from './SearchableSelect'
import { FormMessage } from './FormMessage'

interface ClinicaSearchProps {
  onClinicaSelect?: (clinica: ClinicaItem) => void
  selectedClinica?: ClinicaItem | null
  disabled?: boolean
  forceClinicaId?: number
  modeloExame?: string
}

export default function ClinicaSearch({ onClinicaSelect, selectedClinica, disabled = false, forceClinicaId, modeloExame }: ClinicaSearchProps) {
  const [estados, setEstados] = useState<EstadoItem[]>([])
  const [cidades, setCidades] = useState<CidadeItem[]>([])
  const [bairros, setBairros] = useState<BairroItem[]>([])
  const [clinicas, setClinicas] = useState<ClinicaItem[]>([])

  const [selectedEstado, setSelectedEstado] = useState('')
  const [selectedCidade, setSelectedCidade] = useState('')
  const [selectedBairro, setSelectedBairro] = useState('')
  const [selectedClinicaId, setSelectedClinicaId] = useState<number | null>(null)
  const [filtrosInicializados, setFiltrosInicializados] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  const [loading, setLoading] = useState({
    estados: false,
    cidades: false,
    bairros: false,
    clinicas: false,
  })

  const [error, setError] = useState('')

  // Carregar estados ao montar componente
  useEffect(() => {
    const loadEstados = async () => {
      setLoading(prev => ({ ...prev, estados: true }))
      try {
        const estadosData = await clinicasService.getEstados()
        setEstados(estadosData)
      } catch (error: unknown) {
        console.error('Erro ao carregar estados:', error)
        setError('Erro ao carregar estados')
      } finally {
        setLoading(prev => ({ ...prev, estados: false }))
      }
    }

    loadEstados()
  }, [])

  // Carregar cidades quando estado for selecionado
  useEffect(() => {
    if (!selectedEstado) {
      setCidades([])
      setSelectedCidade('')
      return
    }

    const loadCidades = async () => {
      setLoading(prev => ({ ...prev, cidades: true }))
      try {
        const cidadesData = await clinicasService.getCidadesByEstado(selectedEstado)
        setCidades(cidadesData)
      } catch (error: unknown) {
        console.error('Erro ao carregar cidades:', error)
        setError('Erro ao carregar cidades')
      } finally {
        setLoading(prev => ({ ...prev, cidades: false }))
      }
    }

    loadCidades()
  }, [selectedEstado])

  // Carregar bairros quando cidade for selecionada
  useEffect(() => {
    if (!selectedEstado || !selectedCidade) {
      setBairros([])
      setSelectedBairro('')
      return
    }

    const loadBairros = async () => {
      setLoading(prev => ({ ...prev, bairros: true }))
      try {
        console.log(`🏘️ Buscando bairros para ${selectedCidade} - ${selectedEstado}`)
        const bairrosData = await clinicasService.getBairrosByCidadeEstado(selectedEstado, selectedCidade)
        const safeBairros = Array.isArray(bairrosData) ? bairrosData : []
        console.log(`🏘️ Bairros encontrados: ${safeBairros.length}`)
        setBairros(safeBairros)
      } catch (error: unknown) {
        console.error('Erro ao carregar bairros:', error)
        setError('Erro ao carregar bairros')
      } finally {
        setLoading(prev => ({ ...prev, bairros: false }))
      }
    }

    loadBairros()
  }, [selectedEstado, selectedCidade])

  // Mostrar clínica selecionada se fornecida via prop e preencher filtros
  useEffect(() => {
    if (selectedClinica) {
      // Se a clínica selecionada mudou ou é a primeira vez, atualizar filtros
      if (selectedClinica.id !== selectedClinicaId) {
        setSelectedClinicaId(selectedClinica.id)

        // Preencher filtros apenas se ainda não foram inicializados (primeira vez)
        if (!filtrosInicializados) {
          if (selectedClinica.uf) setSelectedEstado(selectedClinica.uf)
          if (selectedClinica.cidade) setSelectedCidade(selectedClinica.cidade)
          if (selectedClinica.bairro) setSelectedBairro(selectedClinica.bairro)
          setFiltrosInicializados(true)
        }

        // Se a lista estiver vazia, adicionar a clínica selecionada
        if (clinicas.length === 0) {
          setClinicas([selectedClinica])
        }
      }
    }
  }, [selectedClinica, selectedClinicaId, clinicas.length, filtrosInicializados])

  // Limpar filtros quando for exame online
  useEffect(() => {
    if (modeloExame === '1') {
      setSelectedEstado('')
      setSelectedCidade('')
      setSelectedBairro('')
      // Não limpamos as clínicas aqui, pois o outro useEffect vai carregar as online
    }
  }, [modeloExame])

  // Carregar clínicas quando filtros forem aplicados
  useEffect(() => {
    const loadClinicas = async () => {
      setLoading(prev => ({ ...prev, clinicas: true }))
      try {
        let clinicasData: ClinicaItem[] = []

        // Se forceClinicaId estiver definido, buscar apenas essa clínica (não precisa de filtros)
        if (forceClinicaId) {
          try {
            const clinicaForcada = await clinicasService.getClinicaById(forceClinicaId)
            clinicasData = [clinicaForcada]
            console.log('🔒 [ClinicaSearch] Forçando exibição apenas da clínica:', clinicaForcada.nome)
          } catch (error) {
            console.error('❌ [ClinicaSearch] Erro ao buscar clínica forçada:', error)
            clinicasData = []
          }
        } else {
          // Buscar clínicas normalmente com os filtros (precisa de selectedEstado, exceto para Online)
          const isOnline = modeloExame === '1'

          if (!selectedEstado && !isOnline) {
            setClinicas([])
            return
          }

          // Passar limit alto quando buscar por estado ou cidade para garantir todas as clínicas
          // Backend já aplica limite automático, mas deixamos explícito aqui
          const limit = selectedBairro ? undefined : 10000 // Bairro: deixa backend decidir, Estado/Cidade: 10000

          clinicasData = await clinicasService.getClinicasFiltradas({
            estado: selectedEstado || undefined,
            cidade: selectedCidade || undefined,
            bairro: selectedBairro || undefined,
            limit,
            online: isOnline ? true : undefined,
          })
        }

        setClinicas(Array.isArray(clinicasData) ? clinicasData : [])
      } catch (error: unknown) {
        console.error('Erro ao carregar clínicas:', error)

        // Melhor tratamento de erro
        let errorMessage = 'Erro ao carregar clínicas'

        if (error instanceof Error) {
          errorMessage = error.message
        } else if (typeof error === 'object' && error !== null) {
          const errResponse = (error as { response?: { data?: { message?: string; error?: string } } }).response
          if (errResponse?.data?.message) {
            errorMessage = errResponse.data.message
          } else if (errResponse?.data?.error) {
            errorMessage = errResponse.data.error
          }
        } else if (typeof error === 'string') {
          errorMessage = error
        }

        // Formatar mensagem
        errorMessage = String(errorMessage || 'Erro desconhecido')
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, str => str.toUpperCase())
          .replace(/\s+/g, ' ')
          .trim()

        setError(errorMessage)
      } finally {
        setLoading(prev => ({ ...prev, clinicas: false }))
      }
    }

    // Debounce para evitar muitas requisições
    const timeoutId = setTimeout(loadClinicas, 300)
    return () => clearTimeout(timeoutId)
  }, [selectedEstado, selectedCidade, selectedBairro, forceClinicaId, modeloExame])

  // Reset selections quando estado muda
  const handleEstadoChange = (estado: string) => {
    setSelectedEstado(estado)
    setSelectedCidade('')
    setSelectedBairro('')
    setSelectedClinicaId(null)
    setClinicas([])
    setError('')
    setFiltrosInicializados(true)
  }

  // Reset selections quando cidade muda
  const handleCidadeChange = (cidade: string) => {
    setSelectedCidade(cidade)
    // Se cidade for limpa, também limpar bairro
    if (!cidade) {
      setSelectedBairro('')
    }
    setSelectedClinicaId(null)
    setFiltrosInicializados(true)
  }

  // Reset selections quando bairro muda
  const handleBairroChange = (bairro: string) => {
    setSelectedBairro(bairro)
    setSelectedClinicaId(null)
    setFiltrosInicializados(true)
  }

  // Selecionar clínica
  const handleClinicaSelect = (clinica: ClinicaItem) => {
    setSelectedClinicaId(clinica.id)
    onClinicaSelect?.(clinica)
  }

  // Handler para quando não encontrar resultados na busca
  const handleNoResults = (data: {
    searchTerm: string
    field: string
    currentValue: string
    allFilters: Record<string, string>
  }) => {
    // Disparar evento customizado para analytics
    const event = new CustomEvent('clinicaSearchNoResults', {
      detail: {
        searchTerm: data.searchTerm,
        field: data.field,
        currentValue: data.currentValue,
        estado: data.allFilters.estado || selectedEstado,
        cidade: data.allFilters.cidade || selectedCidade,
        bairro: data.allFilters.bairro || selectedBairro,
        timestamp: new Date().toISOString()
      }
    })
    window.dispatchEvent(event)

    // Também logar no console para debug (pode remover depois)
    console.log('🔍 Busca sem resultados:', event.detail)
  }

  // Obter todos os filtros atuais
  const getAllFilters = () => ({
    estado: selectedEstado,
    cidade: selectedCidade,
    bairro: selectedBairro
  })

  // Disparar evento quando busca de clínica não encontrar resultados
  const previousClinicaSearchRef = useRef<string>('')
  useEffect(() => {
    if (searchTerm.length > 0 && clinicas.length > 0) {
      const filteredClinicas = clinicas.filter((clinica) => {
        const search = searchTerm.toLowerCase()
        return (
          clinica.nome.toLowerCase().includes(search) ||
          (clinica.endereco || '').toLowerCase().includes(search) ||
          (clinica.bairro || '').toLowerCase().includes(search) ||
          clinica.cidade.toLowerCase().includes(search)
        )
      })

      // Se não encontrou resultados e o termo mudou
      if (filteredClinicas.length === 0 && searchTerm !== previousClinicaSearchRef.current) {
        previousClinicaSearchRef.current = searchTerm

        const event = new CustomEvent('clinicaSearchNoResults', {
          detail: {
            searchTerm,
            field: 'clinica',
            currentValue: '',
            estado: selectedEstado,
            cidade: selectedCidade,
            bairro: selectedBairro,
            timestamp: new Date().toISOString()
          }
        })
        window.dispatchEvent(event)
        console.log('🔍 Busca de clínica sem resultados:', event.detail)
      }
    } else if (searchTerm.length === 0) {
      previousClinicaSearchRef.current = ''
    }
  }, [searchTerm, clinicas, selectedEstado, selectedCidade, selectedBairro])

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">
          Buscar Clínica
        </h3>
        {clinicas.length > 0 && (
          <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
            {clinicas.length} clínica(s) encontrada(s)
          </span>
        )}
      </div>

      {error && (
        <FormMessage
          type="error"
          message={error}
          onClose={() => setError('')}
        />
      )}

      {disabled && forceClinicaId && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-amber-800">
              <strong>Exames periódicos online</strong> só podem ser realizados na clínica de Santana. Os filtros foram bloqueados automaticamente.
            </p>
          </div>
        </div>
      )}

      {/* Filtros em Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Estado */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Estado *
          </label>
          <SearchableSelect
            options={estados}
            value={selectedEstado}
            onChange={handleEstadoChange}
            getOptionValue={(estado) => estado.uf}
            getOptionLabel={(estado) => `${estado.uf} - ${estado.nome}`}
            placeholder="Selecione"
            disabled={disabled || loading.estados}
            loading={loading.estados}
            emptyMessage="Nenhum estado encontrado"
            onNoResults={handleNoResults}
            fieldName="estado"
            allFilters={getAllFilters()}
          />
        </div>

        {/* Cidade */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Cidade
          </label>
          <SearchableSelect
            options={cidades}
            value={selectedCidade}
            onChange={handleCidadeChange}
            getOptionValue={(cidade) => cidade?.nome || ''}
            getOptionLabel={(cidade) => cidade?.nome || ''}
            placeholder="Todas"
            disabled={disabled || !selectedEstado || loading.cidades}
            loading={loading.cidades}
            emptyMessage="Nenhuma cidade encontrada"
            onNoResults={handleNoResults}
            fieldName="cidade"
            allFilters={getAllFilters()}
            allowClear={true}
          />
        </div>

        {/* Bairro */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Bairro (Opcional)
          </label>
          <SearchableSelect
            options={bairros}
            value={selectedBairro}
            onChange={handleBairroChange}
            getOptionValue={(bairro) => bairro.nome}
            getOptionLabel={(bairro) => bairro.nome}
            placeholder="Todos"
            disabled={disabled || !selectedCidade || loading.bairros}
            loading={loading.bairros}
            emptyMessage="Nenhum bairro encontrado"
            onNoResults={handleNoResults}
            fieldName="bairro"
            allFilters={getAllFilters()}
            allowClear={true}
          />
        </div>
      </div>

      {/* Busca Local */}
      {clinicas.length > 0 && (
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Buscar na lista
          </label>
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Digite o nome ou endereço da clínica..."
              className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white text-gray-900"
            />
            <svg
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Lista de Clínicas em Grid */}
      {(selectedEstado || forceClinicaId || modeloExame === '1') && (
        <div className="mt-6">
          {loading.clinicas ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
              <p className="mt-2 text-gray-600">Buscando clínicas...</p>
            </div>
          ) : clinicas.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200 text-gray-500">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <p className="mt-2">Nenhuma clínica encontrada para os filtros selecionados.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[500px] overflow-y-auto pr-2">
              {clinicas
                .filter((clinica) => {
                  if (!searchTerm) return true
                  const norm = (s?: string | null) =>
                    (s || '')
                      .normalize('NFD')
                      .replace(/[\u0300-\u036f]/g, '')
                      .toLowerCase()
                      .trim()
                  const search = norm(searchTerm)
                  return (
                    norm(clinica.nome).includes(search) ||
                    norm(clinica.endereco).includes(search) ||
                    norm(clinica.bairro).includes(search) ||
                    norm(clinica.cidade).includes(search)
                  )
                })
                .map((clinica) => {
                  const isSelected = selectedClinicaId === clinica.id
                  return (
                    <div
                      key={clinica.id}
                      onClick={() => handleClinicaSelect(clinica)}
                      className={`
                      relative p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 group
                      ${isSelected
                          ? 'border-green-500 bg-green-50 shadow-md'
                          : 'border-gray-200 hover:border-green-300 hover:shadow-sm bg-white'
                        }
                    `}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className={`font-bold text-lg ${isSelected ? 'text-green-900' : 'text-gray-900'}`}>
                              {clinica.nome}
                            </h4>
                            {isSelected && (
                              <span className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full font-medium">
                                Selecionada
                              </span>
                            )}
                          </div>

                          <div className="space-y-1 text-sm text-gray-600">
                            <p className="flex items-start gap-2">
                              <svg className="w-4 h-4 mt-0.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                              <span>
                                {clinica.bairro}, {clinica.cidade} - {clinica.uf}
                              </span>
                            </p>

                            {/* Exibir horários de atendimento */}
                            {clinica.horarios && clinica.horarios.length > 0 && (() => {
                              interface HorarioItem {
                                tipo?: string | null;
                                dia: number;
                                de?: string;
                                ate?: string;
                                intervalo_ini?: string;
                                intervalo_fim?: string;
                              }
                              // Filtrar horários baseado no modeloExame
                              const rawHorarios = clinica.horarios as unknown as HorarioItem[];
                              const horariosFiltrados = rawHorarios.filter((h: HorarioItem) => {
                                if (modeloExame === '1') {
                                  // Online: mostrar apenas horários online
                                  return h.tipo === 'online';
                                } else {
                                  // Presencial: mostrar apenas horários presenciais ou null (retrocompatibilidade)
                                  return h.tipo === 'presencial' || h.tipo === null;
                                }
                              });

                              if (horariosFiltrados.length === 0) return null;

                              const diasSemana = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
                              const horariosAgrupados = horariosFiltrados.reduce<Record<string, HorarioItem[]>>((acc, h) => {
                                const dia = diasSemana[h.dia] || `Dia ${h.dia}`;
                                if (!acc[dia]) acc[dia] = [];
                                acc[dia].push(h);
                                return acc;
                              }, {});

                              return (
                                <div className="mt-2 pt-2 border-t border-gray-200">
                                  <p className="text-xs font-semibold text-gray-700 mb-1">
                                    {modeloExame === '1' ? 'Horários Online' : 'Horários Presenciais'}
                                  </p>
                                  <div className="space-y-1">
                                    {Object.entries(horariosAgrupados).map(([dia, horariosDia]: [string, HorarioItem[]]) => (
                                      <p key={dia} className="text-xs text-gray-600">
                                        <span className="font-medium">{dia}:</span>{' '}
                                        {horariosDia.map((h: HorarioItem, idx: number) => (
                                          <span key={idx}>
                                            {h.de && h.ate ? `${h.de} - ${h.ate}` : 'Fechado'}
                                            {h.intervalo_ini && h.intervalo_fim && ` (Intervalo: ${h.intervalo_ini} - ${h.intervalo_fim})`}
                                            {idx < horariosDia.length - 1 ? ', ' : ''}
                                          </span>
                                        ))}
                                      </p>
                                    ))}
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        </div>

                        <div className={`
                        w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ml-3 transition-colors
                        ${isSelected
                            ? 'bg-green-500 border-green-500 text-white'
                            : 'border-gray-300 group-hover:border-green-400'
                          }
                      `}>
                          {isSelected && (
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

